import React, { useCallback } from "react";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import TabButton from "app/components/Button/TabButton";
import { cls } from "app/utils";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { useHistory, useParams } from "react-router-dom";
import ApplyTemplateDropdown from "../Button/ApplyTemplateDropdown";
import DropdownButton from "../DropdownButton";
import { useActions } from "app/utils/hooks";
import { deleteSpaceTemplate } from "app/actions/spaceTemplate";
import customPrompt from "../inputs/SelectBox/helpers/customPrompt";
import { useTranslation } from "react-i18next";

interface Props {
  hideTabs?: boolean;
  hideHeaderMenu?: boolean;
}

const SpaceTemplatesHeader = ({ hideHeaderMenu, hideTabs }: Props) => {
  const { t } = useTranslation();
  const { selectedTemplate } = useSelector(
    (state: RootState) => state.spaceTemplate,
    shallowEqual,
  );
  const templateActions = useActions({ deleteSpaceTemplate });

  const history = useHistory();

  const route = (path: string) => {
    history.push(path);
  };

  const { id: templateId } = useParams<{ id: string }>();

  const onTimelineClick = () => {
    route(`/space_templates/${templateId}/posts`);
  };

  const onHistoryClick = () => {
    route(`/space_templates/${templateId}/history`);
  };

  const onDelete = useCallback(
    (id: string) => {
      customPrompt({
        message: t(`Are you sure you would like to delete {{name}} ?`, {
          name: selectedTemplate?.name,
        }),
        onYes: () => {
          templateActions
            .deleteSpaceTemplate(id)
            .then(() => route("/space_templates"));
        },
        onCancel: () => {
          return;
        },
      });
    },
    [templateId],
  );

  const menuList = [
    {
      label: t("Edit Name"),
      onClick: () => history.push(`/space_templates/${templateId}/edit`),
    },
    {
      label: t("Delete"),
      className: styles.delete,
      onClick: () => onDelete(templateId),
    },
    {
      label: t("Reorder"),
      onClick: () =>
        history.push(`/space_templates/${templateId}/posts/reorder`),
    },
  ];

  const renderMenu = () => {
    return (
      <ul className={styles.menu}>
        {menuList.map((item) => (
          <li
            key={item.label}
            className={cls(styles.menu_item, item?.className)}
            onClick={item.onClick}
          >
            {item.label}
          </li>
        ))}
      </ul>
    );
  };

  const dropdownButton = () => {
    return (
      <button
        className={cls(
          "btn btn-outline-secondary dropdown-toggle",
          styles.dropdown_button_content,
        )}
      >
        <i className="icon-cog" />
        &nbsp;
        <i className={cls(styles.chevron, "ico ico-chevron")} />
      </button>
    );
  };

  const renderTemplateMenus = () => {
    return (
      <div className={styles.template_menu_wrapper}>
        <ApplyTemplateDropdown
          templateId={templateId}
          templateName={selectedTemplate?.name}
          className={styles.apply_template_btn}
        />
        <DropdownButton
          btnContent={dropdownButton()}
          btnClassName={styles.dropdown_btn}
        >
          {renderMenu()}
        </DropdownButton>
      </div>
    );
  };

  return (
    <PagePanel
      panelClassName={cls({ [styles.page_panel]: !hideTabs })}
      title={selectedTemplate?.name}
      headerChildren={!hideHeaderMenu && renderTemplateMenus()}
      headerWrapperClassName={styles.header_wrapper}
    >
      {!hideTabs && (
        <div className={styles.navbar_wrapper}>
          {onTimelineClick && (
            <TabButton
              label={t("Posts")}
              icon={<i className={cls("tab-icon ico ico-feed-o")} />}
              isActive={window.location.pathname.includes("posts")}
              onClick={onTimelineClick}
              className={styles.mobile}
              labelClassName={styles.tab_label}
            />
          )}
          {onHistoryClick && (
            <TabButton
              label={t("History")}
              icon={<i className={cls("tab-icon ico ico-templates")} />}
              isActive={window.location.pathname.includes("history")}
              onClick={onHistoryClick}
              className={styles.mobile}
              labelClassName={styles.tab_label}
            />
          )}
        </div>
      )}
    </PagePanel>
  );
};

export default SpaceTemplatesHeader;
